import { ErrorBoundary } from '@sentry/react';
import LoadingScreen from 'components/LoadingScreen';
import { ModuleGuard } from 'components/ModuleGuard';
import PageLayout from 'components/PageLayout';
import ActiveFiltersBar from 'components/SearchAndFilters/SelectedFiltersBar';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { Suspense } from 'react';
import { ToastContainer } from 'react-toastify';
import { Row, Col, CardTitle } from 'reactstrap';
import PCComplianceGuardFallback from 'views/Admin/Dashboard/components/PCCompliance/PCComplianceComingSoon';

import BulkActions from './BulkActions';
import Filters from './Filters';
import { useFilterConfig } from './Filters.config';
import FindingInsights from './FindingsInsights';
import PCFindingsDataGrid from './PCFindingsDataGrid';
import {
  filterValuesAtom,
  findingsDataAtom,
  useOnFilterChangeResetRelatedAtoms,
} from './state';

const SelectedFilters: React.FC<{ filtersConfig }> = ({ filtersConfig }) => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const resetOnFilterChangeAtoms = useOnFilterChangeResetRelatedAtoms();

  return (
    <Suspense fallback={<></>}>
      <ActiveFiltersBar
        filtersConfig={filtersConfig}
        onFiltersReset={() => {
          setFilterValues({ ...filtersConfig.getActiveFilterValues() });
          resetOnFilterChangeAtoms();
        }}
      />
    </Suspense>
  );
};

const ListingCount: React.FC = () => {
  const listingData = useAtomValue(findingsDataAtom);
  return <>{listingData?.loading ? '...' : listingData.count}</>;
};

function PCFindingsListing() {
  const filtersConfig = useFilterConfig();

  return (
    <ModuleGuard module="pcm" fallback={<PCComplianceGuardFallback />}>
      <PageLayout
        id="pc_findings_listing"
        heading={
          <Row>
            <Col md="auto" sm="12">
              <CardTitle>
                <h4>
                  Policy Compliance Findings
                  <label id="counts" className="label btn">
                    <Suspense fallback={<>...</>}>
                      <ListingCount />
                    </Suspense>
                  </label>
                </h4>
              </CardTitle>
            </Col>
          </Row>
        }
        subheading="Identify and manage your Policy Compliance findings."
        filteringOptions={
          <Suspense fallback={<>...</>}>
            <Filters filtersConfig={filtersConfig} />
          </Suspense>
        }
      >
        <Suspense fallback={<LoadingScreen />}>
          <div className="container-fluid p-0">
            <div className="row mb-1">
              {/* Expanding Column */}
              <div className="col">
                <SelectedFilters filtersConfig={filtersConfig} />
              </div>

              {/* Fixed Column with Right-Aligned Content */}
              <div className="col-auto text-right align-baseline">
                <Suspense fallback={<>...</>}>
                  <BulkActions />
                </Suspense>
              </div>
            </div>
            {/* Add the summary component here */}
            <Suspense fallback={<LoadingScreen />}>
              <FindingInsights />
            </Suspense>
            <Suspense fallback={<LoadingScreen />}>
              <PCFindingsDataGrid />
            </Suspense>
          </div>
        </Suspense>
      </PageLayout>
    </ModuleGuard>
  );
}

const PCFindings: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <div className="content">
        <ErrorBoundary>
          <PCFindingsListing />
        </ErrorBoundary>
      </div>
      <ToastContainer />
    </Suspense>
  );
};

export default PCFindings;
